import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useMovieDetail } from '../hooks/useMovie';
import { usePageTitle } from '../hooks/usePageTitle';
import { cn } from '../lib/utils';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function formatTime(seconds) {
  if (!seconds || !isFinite(seconds) || isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function TrailerPage() {
  const { movieId } = useParams();
  const navigate = useNavigate();
  const { data: movie, isLoading } = useMovieDetail(movieId);

  usePageTitle(movie?.Title ? `${movie.Title} Trailer · Kast` : 'Trailer · Kast');

  const containerRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const progressRef = React.useRef(null);
  const hideTimerRef = React.useRef(null);
  const isDraggingRef = React.useRef(false);

  const [playing, setPlaying] = React.useState(true);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [buffered, setBuffered] = React.useState(0);
  const [volume, setVolume] = React.useState(1);
  const [prevVolume, setPrevVolume] = React.useState(1);
  const [muted, setMuted] = React.useState(false);
  const [playbackSpeed, setPlaybackSpeed] = React.useState(1);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isBuffering, setIsBuffering] = React.useState(true);
  const [showControls, setShowControls] = React.useState(true);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [hoverTime, setHoverTime] = React.useState(null);
  const [hoverPos, setHoverPos] = React.useState(0);
  const [centerIcon, setCenterIcon] = React.useState(null);

  // Lock body scroll while the full-screen player is mounted
  React.useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
      videoRef.current.volume = volume;
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [muted, volume, playbackSpeed]);

  const resetHideTimer = React.useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (playing && !isDraggingRef.current && !settingsOpen) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 3500);
    }
  }, [playing, settingsOpen]);

  React.useEffect(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (playing && !settingsOpen) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 3500);
    }
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [playing, settingsOpen]);

  const triggerCenterFeedback = (iconName) => {
    setCenterIcon(iconName);
    setTimeout(() => setCenterIcon(null), 600);
  };

  const togglePlay = React.useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => { setPlaying(true); triggerCenterFeedback('ph:play-fill'); }).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
      triggerCenterFeedback('ph:pause-fill');
    }
  }, []);

  const skip = React.useCallback((sec) => {
    const v = videoRef.current;
    if (!v) return;
    const targetDur = v.duration || 0;
    const newTime = Math.max(0, Math.min(targetDur, v.currentTime + sec));
    v.currentTime = newTime;
    setCurrentTime(newTime);
    triggerCenterFeedback(sec > 0 ? 'ph:fast-forward-bold' : 'ph:rewind-bold');
  }, []);

  const toggleMute = React.useCallback(() => {
    if (muted || volume === 0) {
      const restored = prevVolume > 0 ? prevVolume : 1;
      setVolume(restored);
      setMuted(false);
    } else {
      setPrevVolume(volume);
      setMuted(true);
    }
  }, [muted, volume, prevVolume]);

  const handleVolumeChange = React.useCallback((newVol) => {
    setVolume(newVol);
    setMuted(newVol === 0);
    if (newVol > 0) setPrevVolume(newVol);
  }, []);

  const toggleFullscreen = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const isFull = document.fullscreenElement || document.webkitFullscreenElement;
    if (isFull) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    }
  }, []);

  React.useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  React.useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.key === ' ' || e.key === 'k' || e.key === 'K') { e.preventDefault(); togglePlay(); }
      else if (e.key === 'm' || e.key === 'M') { e.preventDefault(); toggleMute(); }
      else if (e.key === 'f' || e.key === 'F') { e.preventDefault(); toggleFullscreen(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); skip(10); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); skip(-10); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); handleVolumeChange(Math.min(1, volume + 0.1)); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); handleVolumeChange(Math.max(0, volume - 0.1)); }
      else if (e.key === 'Escape') { navigate(-1); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [togglePlay, toggleMute, toggleFullscreen, skip, handleVolumeChange, volume, navigate]);

  const seekToClientX = React.useCallback((clientX) => {
    const bar = progressRef.current;
    const v = videoRef.current;
    if (!bar || !v) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const targetDur = duration || v.duration || 0;
    if (targetDur > 0 && isFinite(targetDur)) {
      const newTime = ratio * targetDur;
      v.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [duration]);

  const handleProgressMouseDown = (e) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    seekToClientX(e.clientX);

    const onMouseMove = (moveEvent) => seekToClientX(moveEvent.clientX);
    const onMouseUp = (upEvent) => {
      isDraggingRef.current = false;
      seekToClientX(upEvent.clientX);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      resetHideTimer();
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleProgressHover = (e) => {
    const bar = progressRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const targetDur = duration || videoRef.current?.duration || 0;
    setHoverPos(ratio * 100);
    setHoverTime(ratio * targetDur);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferProgress = duration > 0 ? (buffered / duration) * 100 : 0;

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
        <Icon icon="ph:spinner-gap-bold" className="size-10 animate-spin text-white/70" />
      </div>
    );
  }

  if (!movie || !movie.TrailerUrl) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-black p-6 text-center text-white">
        <Icon icon="ph:film-slate" className="size-12 text-white/30" />
        <h2 className="text-xl font-bold">No trailer available</h2>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/20"
        >
          <Icon icon="ph:arrow-left-bold" className="size-4" />
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onMouseMove={resetHideTimer}
      className="fixed inset-0 z-50 size-full overflow-hidden bg-black text-white select-none"
    >
      {/* Buffering spinner */}
      {isBuffering && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
          <div className="flex size-16 items-center justify-center rounded-full border border-white/15 bg-black/70 backdrop-blur-md">
            <Icon icon="ph:spinner-gap-bold" className="size-8 animate-spin text-white" />
          </div>
        </div>
      )}

      {/* Center play/pause/skip feedback */}
      {centerIcon && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center animate-in zoom-in-50 fade-in duration-200">
          <div className="flex size-20 items-center justify-center rounded-full border border-white/20 bg-black/70 backdrop-blur-md">
            <Icon icon={centerIcon} className="size-9 text-white" />
          </div>
        </div>
      )}

      {/* Video — same src wiring as the version that was confirmed working: a plain <video>
          pointed straight at the raw TrailerUrl, no Hls.js / stream proxy involved. Only the
          surrounding chrome below is custom; playback itself is untouched. */}
      <div
        className="relative size-full cursor-pointer"
        onClick={togglePlay}
        onDoubleClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          if (e.clientX - rect.left < rect.width / 2) skip(-10);
          else skip(10);
        }}
      >
        <video
          ref={videoRef}
          src={movie.TrailerUrl}
          autoPlay
          playsInline
          className="size-full object-contain"
          onError={() => setIsBuffering(false)}
          onPlay={() => { setPlaying(true); setIsBuffering(false); }}
          onPause={() => setPlaying(false)}
          onPlaying={() => { setPlaying(true); setIsBuffering(false); }}
          onWaiting={() => setIsBuffering(true)}
          onCanPlay={() => setIsBuffering(false)}
          onTimeUpdate={() => {
            const v = videoRef.current;
            if (!v || isDraggingRef.current) return;
            setCurrentTime(v.currentTime);
            if (v.duration && isFinite(v.duration)) setDuration(v.duration);
            if (v.buffered.length > 0) setBuffered(v.buffered.end(v.buffered.length - 1));
          }}
          onLoadedMetadata={() => {
            const v = videoRef.current;
            if (v?.duration && isFinite(v.duration)) setDuration(v.duration);
          }}
          onDurationChange={() => {
            const v = videoRef.current;
            if (v?.duration && isFinite(v.duration)) setDuration(v.duration);
          }}
          onEnded={() => setPlaying(false)}
        >
          <track kind="captions" />
        </video>
      </div>

      {/* Top header bar */}
      <div
        className={cn(
          'absolute inset-x-0 top-0 z-50 flex items-center justify-between px-6 pt-5 pb-12 bg-linear-to-b from-black/90 via-black/50 to-transparent transition-opacity duration-300',
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back to movie details"
            className="flex size-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md transition-colors hover:border-white/30 hover:bg-white/20"
          >
            <Icon icon="ph:arrow-left-bold" className="size-5" />
          </button>

          <div>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/25 px-2.5 py-0.5 text-[11px] font-bold text-amber-300">
                <Icon icon="ph:film-reel-bold" className="size-3" />
                TRAILER
              </div>
            </div>
            <h1 className="mt-0.5 text-lg sm:text-xl text-white font-semibold leading-tight">
              {movie.Title}
            </h1>
          </div>
        </div>
      </div>

      {/* Bottom controls bar */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 z-40 bg-linear-to-t from-black/95 via-black/70 to-transparent pt-14 pb-5 px-6 transition-opacity duration-300',
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrubber */}
        <div
          ref={progressRef}
          className="group/progress relative h-2 cursor-pointer rounded-full bg-white/20 transition-all hover:h-3"
          onMouseDown={handleProgressMouseDown}
          onMouseMove={handleProgressHover}
          onMouseLeave={() => setHoverTime(null)}
        >
          <div className="absolute inset-y-0 left-0 rounded-full bg-white/25" style={{ width: `${bufferProgress}%` }} />
          <div className="absolute inset-y-0 left-0 rounded-full brand-sweep" style={{ width: `${progress}%` }} />
          <div
            className="absolute top-1/2 -translate-y-1/2 size-4 -ml-2 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.9)] opacity-0 transition-opacity group-hover/progress:opacity-100"
            style={{ left: `${progress}%` }}
          />
          {hoverTime !== null && (
            <div
              className="pointer-events-none absolute -top-9 -translate-x-1/2 rounded-md bg-black/90 px-2.5 py-1 text-xs font-semibold text-white shadow-xl border border-white/15"
              style={{ left: `${hoverPos}%` }}
            >
              {formatTime(hoverTime)}
            </div>
          )}
        </div>

        {/* Action buttons row */}
        <div className="mt-3.5 flex items-center gap-2">
          <button
            type="button"
            onClick={togglePlay}
            className="flex size-10 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white"
            aria-label={playing ? 'Pause' : 'Play'}
          >
            <Icon icon={playing ? 'ph:pause-fill' : 'ph:play-fill'} className="size-6" />
          </button>

          <button
            type="button"
            onClick={() => skip(-10)}
            className="flex size-9 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/15 hover:text-white"
            aria-label="Rewind 10 seconds"
          >
            <Icon icon="ph:rewind-bold" className="size-5" />
          </button>

          <button
            type="button"
            onClick={() => skip(10)}
            className="flex size-9 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/15 hover:text-white"
            aria-label="Forward 10 seconds"
          >
            <Icon icon="ph:fast-forward-bold" className="size-5" />
          </button>

          <div className="group/vol relative flex items-center gap-1.5 ml-1">
            <button
              type="button"
              onClick={toggleMute}
              className="flex size-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white"
              aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}
            >
              <Icon
                icon={
                  muted || volume === 0
                    ? 'ph:speaker-simple-x-bold'
                    : volume < 0.5
                      ? 'ph:speaker-simple-low-bold'
                      : 'ph:speaker-simple-high-bold'
                }
                className="size-5"
              />
            </button>
            <div className="w-0 overflow-hidden transition-all duration-200 group-hover/vol:w-20 group-focus-within/vol:w-20">
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="h-1.5 w-20 cursor-pointer accent-white"
                aria-label="Volume slider"
              />
            </div>
          </div>

          <span className="ml-2 text-xs sm:text-sm font-medium tabular-nums text-white/75">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <div className="flex-1" />

          {/* Speed settings */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setSettingsOpen((s) => !s)}
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium backdrop-blur-md transition-colors',
                settingsOpen ? 'bg-white text-black' : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
              )}
              title="Playback speed"
              aria-label="Playback speed"
            >
              <Icon icon="ph:gauge-bold" className="size-4" />
              <span className="hidden sm:inline">{playbackSpeed === 1 ? 'Normal' : `${playbackSpeed}x`}</span>
            </button>

            {settingsOpen && (
              <div
                className="absolute right-0 bottom-12 z-50 w-40 overflow-hidden rounded-2xl border border-white/15 bg-black/90 p-2 shadow-2xl backdrop-blur-2xl animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setPlaybackSpeed(s); setSettingsOpen(false); }}
                    className={cn(
                      'flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors',
                      playbackSpeed === s ? 'bg-white/15 font-semibold text-white' : 'text-white/80 hover:bg-white/10'
                    )}
                  >
                    <span>{s === 1 ? '1x (Normal)' : `${s}x`}</span>
                    {playbackSpeed === s && <Icon icon="ph:check-bold" className="size-4 text-violet" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={toggleFullscreen}
            className="flex size-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white"
            aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            <Icon icon={isFullscreen ? 'ph:corners-in-bold' : 'ph:corners-out-bold'} className="size-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
